// src/app/modules/adminrh/parametres/ParamAvantagesSociaux/ParamAvantagesSociaux.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  X, Plus, Trash2, Edit, Gift, Heart, Bike, Home,
  Coffee, Settings, Baby, Plane, AlertCircle,
} from "lucide-react";
import Select from "react-select";
import Swal from "sweetalert2";

import "../parametresStyles/ParamAvantagesSociaux.css";

import {
  fetchParamAvantagesRequest,
  createAvCatRequest, updateAvCatRequest, deleteAvCatRequest,
  createAvRegleRequest, updateAvRegleRequest, deleteAvRegleRequest,
  createAvantageRequest, updateAvantageRequest, deleteAvantageRequest,
} from "../../../../redux/actions/paramAvantagesActions";

const ParamAvantagesSociaux = () => {
  const dispatch = useDispatch();

  const { loading, error, categories, regles, avantages } = useSelector(
      (state) => state.paramAvantagessociaux || {}
  );

  const [activeTab, setActiveTab] = useState("categories");
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState("");
  const [formData, setFormData] = useState({});

  useEffect(() => { dispatch(fetchParamAvantagesRequest()); }, [dispatch]);

  useEffect(() => {
    if (error) Swal.fire({ icon: "error", title: "Erreur", text: String(error) });
  }, [error]);

  /* ── helpers ── */
  const tabs = useMemo(() => [
    { id: "avantages",  label: "Avantages",       icon: Gift },
    { id: "regles",     label: "Règles générales", icon: AlertCircle },
    { id: "categories", label: "Catégories",       icon: Settings },
  ], []);

  const getIconComponent = (name) => {
    const map = { Coffee, Heart, Bike, Home, Gift, Baby, Plane, Settings };
    return map[name] || Settings;
  };

  const iconOptions = useMemo(() => [
    { value: "Coffee", label: "Café",   Icon: Coffee },
    { value: "Heart",  label: "Santé",  Icon: Heart },
    { value: "Bike",   label: "Vélo",   Icon: Bike },
    { value: "Home",   label: "Maison", Icon: Home },
    { value: "Gift",   label: "Cadeau", Icon: Gift },
    { value: "Baby",   label: "Bébé",   Icon: Baby },
    { value: "Plane",  label: "Avion",  Icon: Plane },
  ], []);

  const frequencyOptions = useMemo(() => [
    { value: "MENSUEL", label: "Mensuel" },
    { value: "ANNUEL",  label: "Annuel" },
    { value: "JOUR",    label: "Jour" },
    { value: "UNIQUE",  label: "Unique" },
  ], []);

  const categorieOptions = useMemo(() =>
          (categories || []).map((c) => ({ value: c.id, label: c.nom, color: c.couleur, iconName: c.icone })),
      [categories]
  );
  const categorieOptionsActives = useMemo(() =>
          (categories || []).filter((c) => c.actif).map((c) => ({ value: c.id, label: c.nom, color: c.couleur, iconName: c.icone })),
      [categories]
  );
  const regleOptions = useMemo(() =>
          (regles || []).map((r) => ({ value: r.id, label: r.valeur ? `${r.nom} → ${r.valeur}` : r.nom })),
      [regles]
  );

  /* ── modal ── */
  const openModal = (type, data = {}) => {
    setModalType(type);
    if (type === "avantage") {
      setFormData({
        id: data.id, categorieId: data.categorieId ?? data.categorie?.id ?? "",
        regleId: data.regleId ?? data.regle?.id ?? "",
        nom: data.nom || "", code: data.code || "", description: data.description || "",
        montant: data.montant ?? "", frequence: data.frequence || "MENSUEL", actif: data.actif ?? true,
      });
    } else if (type === "categorie") {
      setFormData({
        id: data.id, nom: data.nom || "", code: data.code || "",
        description: data.description || "", icone: data.icone || "",
        couleur: data.couleur || "#f59e0b", actif: data.actif ?? true,
      });
    } else if (type === "regle") {
      setFormData({ id: data.id, nom: data.nom || "", condition: data.condition || "", valeur: data.valeur || "" });
    } else {
      setFormData(data);
    }
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); setModalType(""); setFormData({}); };

  /* ── submit ── */
  const handleSubmit = (e) => {
    e.preventDefault();

    if (modalType === "avantage") {
      if (!formData.regleId) {
        Swal.fire({ icon: "warning", title: "Règle obligatoire", text: "Veuillez sélectionner une règle." });
        return;
      }
      const payload = {
        categorieId: Number(formData.categorieId), regleId: Number(formData.regleId),
        nom: formData.nom, code: formData.code, description: formData.description || null,
        montant: formData.montant === "" ? null : Number(formData.montant),
        frequence: formData.frequence, actif: !!formData.actif,
      };
      if (formData.id) dispatch(updateAvantageRequest(formData.id, payload));
      else             dispatch(createAvantageRequest(payload));
    }

    if (modalType === "categorie") {
      const payload = {
        nom: formData.nom, code: formData.code, description: formData.description || null,
        icone: formData.icone, couleur: formData.couleur, actif: !!formData.actif,
      };
      if (formData.id) dispatch(updateAvCatRequest(formData.id, payload));
      else             dispatch(createAvCatRequest(payload));
    }

    if (modalType === "regle") {
      const payload = { nom: formData.nom, condition: formData.condition || null, valeur: formData.valeur || null };
      if (formData.id) dispatch(updateAvRegleRequest(formData.id, payload));
      else             dispatch(createAvRegleRequest(payload));
    }

    closeModal();
  };

  /* ── delete ── */
  const handleDelete = (type, id) => {
    Swal.fire({
      icon: "warning", title: "Confirmation", text: "Voulez-vous vraiment supprimer ?",
      showCancelButton: true, confirmButtonText: "Supprimer", cancelButtonText: "Annuler",
      confirmButtonColor: "#ee645f", cancelButtonColor: "#204385",
    }).then((res) => {
      if (!res.isConfirmed) return;
      if (type === "categorie") dispatch(deleteAvCatRequest(id));
      if (type === "regle")     dispatch(deleteAvRegleRequest(id));
      if (type === "avantage")  dispatch(deleteAvantageRequest(id));
    });
  };

  /* ── toggle actif ── */
  const toggleActifCategorie = (cat) => {
    dispatch(updateAvCatRequest(cat.id, {
      nom: cat.nom, code: cat.code, description: cat.description || null,
      icone: cat.icone, couleur: cat.couleur, actif: !cat.actif,
    }));
  };
  const toggleActifAvantage = (av) => {
    dispatch(updateAvantageRequest(av.id, {
      categorieId: Number(av.categorieId), regleId: Number(av.regleId),
      nom: av.nom, code: av.code, description: av.description || null,
      montant: av.montant == null ? null : Number(av.montant),
      frequence: av.frequence, actif: !av.actif,
    }));
  };

  /* ══════════════════════════════════════════
     MODAL
  ══════════════════════════════════════════ */
  const renderModal = () => {
    if (!showModal) return null;

    const modalConfig = {
      categorie: {
        title: formData.id ? "Modifier la catégorie" : "Nouvelle catégorie",
        fields: [
          { name: "nom",         label: "Nom",         type: "text",        required: true },
          { name: "code",        label: "Code",        type: "text",        required: true },
          { name: "description", label: "Description", type: "textarea",    required: false },
          { name: "icone",       label: "Icône",       type: "select-icon", required: true },
          { name: "couleur",     label: "Couleur",     type: "color",       required: true },
          { name: "actif",       label: "Actif",       type: "toggle-actif" },
        ],
      },
      regle: {
        title: formData.id ? "Modifier la règle" : "Nouvelle règle",
        fields: [
          { name: "nom",       label: "Nom",       type: "text",     required: true },
          { name: "condition", label: "Condition", type: "textarea", required: false },
          { name: "valeur",    label: "Valeur",    type: "text",     required: false },
        ],
      },
      avantage: {
        title: formData.id ? "Modifier l'avantage" : "Nouvel avantage",
        fields: [
          { name: "categorieId", label: "Catégorie",             type: "select-categorie",  required: true },
          { name: "regleId",     label: "Règle (obligatoire)",   type: "select-regle",      required: true },
          { name: "nom",         label: "Nom",                   type: "text",              required: true },
          { name: "code",        label: "Code",                  type: "text",              required: true },
          { name: "description", label: "Description",           type: "textarea",          required: false },
          { name: "montant",     label: "Montant (DHS)",         type: "number", step:"0.01",required: false },
          { name: "frequence",   label: "Fréquence",             type: "select-frequence",  required: true },
          { name: "actif",       label: "Actif",                 type: "toggle-actif" },
        ],
      },
    };

    const config = modalConfig[modalType];
    if (!config) return null;

    const renderField = (field) => {
      const value = formData[field.name];

      if (field.type === "toggle-actif") {
        return (
            <div className="pcField" key={field.name}>
              <label>{field.label}</label>
              <label className="pasSwitch">
                <input
                    type="checkbox"
                    checked={!!formData.actif}
                    onChange={(e) => setFormData((p) => ({ ...p, actif: e.target.checked }))}
                />
                <span className="pasSwitch__ui" />
                <span className="pasSwitch__text">{formData.actif ? "Actif" : "Inactif"}</span>
              </label>
            </div>
        );
      }

      if (field.type === "textarea") {
        return (
            <div className="pcField" key={field.name}>
              <label>{field.label}</label>
              <textarea
                  rows={2}
                  value={value || ""}
                  onChange={(e) => setFormData((p) => ({ ...p, [field.name]: e.target.value }))}
                  required={field.required}
              />
            </div>
        );
      }

      if (field.type === "select-icon") {
        const selected = iconOptions.find((o) => o.value === value) || null;
        return (
            <div className="pcField" key={field.name}>
              <label>{field.label}</label>
              <Select
                  classNamePrefix="pasSelect"
                  value={selected}
                  options={iconOptions}
                  onChange={(opt) => setFormData((p) => ({ ...p, [field.name]: opt?.value || "" }))}
                  placeholder="Sélectionner…"
                  isSearchable
                  formatOptionLabel={(opt) => (
                      <div className="pasOpt"><opt.Icon size={15} /><span>{opt.label}</span></div>
                  )}
              />
            </div>
        );
      }

      if (field.type === "select-categorie") {
        const opts = modalType === "avantage" ? categorieOptionsActives : categorieOptions;
        const selected = opts.find((o) => o.value === value) || null;
        return (
            <div className="pcField" key={field.name}>
              <label>{field.label}</label>
              <Select
                  classNamePrefix="pasSelect"
                  value={selected}
                  options={opts}
                  onChange={(opt) => setFormData((p) => ({ ...p, [field.name]: opt?.value || "" }))}
                  placeholder="Sélectionner…"
                  isSearchable
                  formatOptionLabel={(opt) => {
                    const Icon = getIconComponent(opt.iconName);
                    return (
                        <div className="pasOpt">
                          <Icon size={15} />
                          <span className="pasOpt__dot" style={{ backgroundColor: opt.color }} />
                          <span>{opt.label}</span>
                        </div>
                    );
                  }}
              />
            </div>
        );
      }

      if (field.type === "select-regle") {
        const selected = regleOptions.find((o) => o.value === value) || null;
        return (
            <div className="pcField" key={field.name}>
              <label>{field.label}</label>
              <Select
                  classNamePrefix="pasSelect"
                  value={selected}
                  options={regleOptions}
                  onChange={(opt) => setFormData((p) => ({ ...p, [field.name]: opt?.value || "" }))}
                  placeholder="Sélectionner une règle…"
                  isSearchable
              />
            </div>
        );
      }

      if (field.type === "select-frequence") {
        const selected = frequencyOptions.find((o) => o.value === value) || null;
        return (
            <div className="pcField" key={field.name}>
              <label>{field.label}</label>
              <Select
                  classNamePrefix="pasSelect"
                  value={selected}
                  options={frequencyOptions}
                  onChange={(opt) => setFormData((p) => ({ ...p, [field.name]: opt?.value || "" }))}
                  placeholder="Sélectionner…"
                  isSearchable
              />
            </div>
        );
      }

      return (
          <div className="pcField" key={field.name}>
            <label>{field.label}</label>
            <input
                type={field.type || "text"}
                step={field.step}
                value={value ?? ""}
                onChange={(e) => setFormData((p) => ({ ...p, [field.name]: e.target.value }))}
                required={field.required}
            />
          </div>
      );
    };

    return (
        <div className="pcModal" onMouseDown={closeModal}>
          <div className="pcModal__panel" onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="pcModal__header">
              <div className="pcModal__title">{config.title}</div>
              <button className="pcIconBtn" onClick={closeModal} type="button" aria-label="Fermer">
                <X size={16} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="pcModal__body">
                <div className="pasForm">
                  {config.fields.map(renderField)}
                </div>
              </div>
              <div className="pcModal__footer">
                <button className="pcBtn pcBtn--ghost" type="button" onClick={closeModal}>
                  Annuler
                </button>
                <button className="pcBtn pcBtn--primary" type="submit" disabled={loading}>
                  {formData.id ? "Enregistrer" : "Ajouter"}
                </button>
              </div>
            </form>
          </div>
        </div>
    );
  };

  /* ══════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════ */
  return (
      <div className="pcPage">
        {/* Header */}
        <div className="pcHeader">
          <div>
            <h1>Paramétrage des avantages sociaux</h1>
            <p>Gérez les avantages, primes et politiques RH.</p>
            {loading && <div className="pcMuted" style={{ marginTop: 6 }}>Chargement…</div>}
          </div>
        </div>

        {/* Card */}
        <div className="pcWrap">
          <div className="pcCard">
            {/* Tabs */}
            <div className="pcTabs">
              {tabs.map((t) => {
                const Icon = t.icon;
                return (
                    <button
                        key={t.id}
                        className={`pcTab ${activeTab === t.id ? "is-active" : ""}`}
                        onClick={() => setActiveTab(t.id)}
                        type="button"
                    >
                      <Icon size={15} />{t.label}
                    </button>
                );
              })}
            </div>

            <div className="pcBody">

              {/* ── AVANTAGES ── */}
              {activeTab === "avantages" && (
                  <div>
                    <div className="pcTopRow">
                      <h2 className="pcTitle">Liste des avantages</h2>
                      <button className="pcBtn pcBtn--primary" type="button" onClick={() => openModal("avantage")} disabled={loading}>
                        <Plus size={14} /> Ajouter
                      </button>
                    </div>

                    <div className="pasSections">
                      {(categories || []).filter((c) => c.actif).map((cat) => {
                        const avs = (avantages || []).filter((a) => a.categorieId === cat.id);
                        if (avs.length === 0) return null;
                        const CatIcon = getIconComponent(cat.icone);
                        return (
                            <div className="pasSection" key={cat.id}>
                              <div className="pasSection__head">
                                <div className="pasSection__badge" style={{ backgroundColor: cat.couleur }}>
                                  <CatIcon size={15} />
                                </div>
                                <div className="pasSection__name">{cat.nom}</div>
                              </div>

                              <div className="pasGrid">
                                {avs.map((av) => (
                                    <div className="pasBox" key={av.id}>
                                      <div className="pasBox__head">
                                        <div>
                                          <div className="pasBox__titleRow">
                                            <span className="pasCode" style={{ backgroundColor: cat.couleur }}>{av.code}</span>
                                            <span className="pcStrong">{av.nom}</span>
                                          </div>
                                          {av.description && <div className="pcMuted" style={{ marginTop: 3 }}>{av.description}</div>}
                                        </div>
                                        <button
                                            className={`pcPill ${av.actif ? "is-on" : "is-off"}`}
                                            type="button"
                                            onClick={() => toggleActifAvantage(av)}
                                        >
                                          {av.actif ? "Actif" : "Inactif"}
                                        </button>
                                      </div>

                                      <div className="pasBox__grid">
                                        {av.montant != null && (
                                            <div className="pasMini">
                                              <div className="pasMini__label">Montant</div>
                                              <div className="pasMini__value pcAccent">{av.montant} DHS</div>
                                            </div>
                                        )}
                                        <div className="pasMini">
                                          <div className="pasMini__label">Fréquence</div>
                                          <div className="pasMini__value">{av.frequence}</div>
                                        </div>
                                        <div className="pasMini pasMini--wide">
                                          <div className="pasMini__label">Règle</div>
                                          <div className="pasElig">
                                            {av.regle?.nom
                                                ? <span className="pasTag">{av.regle.nom}</span>
                                                : <span className="pcMuted">—</span>}
                                            {av.regle?.valeur && <span className="pasTag pasTag--muted">{av.regle.valeur}</span>}
                                          </div>
                                        </div>
                                      </div>

                                      <div className="pasBox__actions">
                                        <button className="pcIconBtn" type="button" title="Modifier" onClick={() => openModal("avantage", av)}>
                                          <Edit size={15} />
                                        </button>
                                        <button className="pcIconBtn pcIconBtn--danger" type="button" title="Supprimer" onClick={() => handleDelete("avantage", av.id)}>
                                          <Trash2 size={15} />
                                        </button>
                                      </div>
                                    </div>
                                ))}
                              </div>
                            </div>
                        );
                      })}

                      {(avantages || []).length === 0 && (
                          <div className="pcEmpty">Aucun avantage configuré</div>
                      )}
                    </div>
                  </div>
              )}

              {/* ── RÈGLES ── */}
              {activeTab === "regles" && (
                  <div>
                    <div className="pcTopRow">
                      <h2 className="pcTitle">Règles générales</h2>
                      <button className="pcBtn pcBtn--primary" type="button" onClick={() => openModal("regle")} disabled={loading}>
                        <Plus size={14} /> Ajouter
                      </button>
                    </div>

                    <div className="pasList">
                      {(regles || []).map((r) => (
                          <div className="pasRow" key={r.id}>
                            <div className="pasRow__main">
                              <div className="pcStrong">{r.nom}</div>
                              <div className="pasRow__sub">
                                {r.condition && <span className="pcMuted">{r.condition}</span>}
                                {r.valeur    && <span className="pcMuted"> — {r.valeur}</span>}
                              </div>
                            </div>
                            <div className="pasRow__actions">
                              <button className="pcIconBtn" type="button" title="Modifier" onClick={() => openModal("regle", r)}>
                                <Edit size={15} />
                              </button>
                              <button className="pcIconBtn pcIconBtn--danger" type="button" title="Supprimer" onClick={() => handleDelete("regle", r.id)}>
                                <Trash2 size={15} />
                              </button>
                            </div>
                          </div>
                      ))}
                      {(regles || []).length === 0 && <div className="pcEmpty">Aucune règle configurée</div>}
                    </div>
                  </div>
              )}

              {/* ── CATÉGORIES ── */}
              {activeTab === "categories" && (
                  <div>
                    <div className="pcTopRow">
                      <h2 className="pcTitle">Catégories d'avantages</h2>
                      <button className="pcBtn pcBtn--primary" type="button" onClick={() => openModal("categorie")} disabled={loading}>
                        <Plus size={14} /> Ajouter
                      </button>
                    </div>

                    <div className="pasCatGrid">
                      {(categories || []).map((cat) => {
                        const Icon = getIconComponent(cat.icone);
                        return (
                            <div className="pasCat" key={cat.id}>
                              <div className="pasCat__left">
                                <div className="pasCat__icon" style={{ backgroundColor: cat.couleur }}>
                                  <Icon size={16} />
                                </div>
                                <div>
                                  <div className="pcStrong" style={{ marginBottom: 5 }}>{cat.nom}</div>
                                  <button
                                      className={`pcPill ${cat.actif ? "is-on" : "is-off"}`}
                                      type="button"
                                      onClick={() => toggleActifCategorie(cat)}
                                  >
                                    {cat.actif ? "Actif" : "Inactif"}
                                  </button>
                                </div>
                              </div>
                              <div className="pasCat__actions">
                                <button className="pcIconBtn" type="button" title="Modifier" onClick={() => openModal("categorie", cat)}>
                                  <Edit size={15} />
                                </button>
                                <button className="pcIconBtn pcIconBtn--danger" type="button" title="Supprimer" onClick={() => handleDelete("categorie", cat.id)}>
                                  <Trash2 size={15} />
                                </button>
                              </div>
                            </div>
                        );
                      })}
                      {(categories || []).length === 0 && <div className="pcEmpty">Aucune catégorie configurée</div>}
                    </div>
                  </div>
              )}

            </div>
          </div>
        </div>

        {renderModal()}
      </div>
  );
};

export default ParamAvantagesSociaux;